// Shared TTS setup for dictation and fortælling rounds.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDictationAudio } from './audio';
import { defaultPreferences } from './storage';
import type { UserProfile } from '../types';
import type { DictationAudioLocale } from './audio/dictationAudio';

export interface UseDictationAudioOptions {
  audioLocale?: DictationAudioLocale;
}

export interface UseDictationAudioResult {
  voicesReady: boolean;
  /** User wants audio and a Danish voice is available. */
  audioAvailable: boolean;
  /** Voices loaded but no Danish voice (show amber banner). */
  showNoVoiceBanner: boolean;
  audioEnabled: boolean;
  speak: (text: string) => Promise<void>;
  cancel: () => void;
}

export function useDictationAudio(
  user: UserProfile,
  options: UseDictationAudioOptions = {},
): UseDictationAudioResult {
  const audio = useMemo(() => getDictationAudio(), []);
  const prefs = user.preferences ?? defaultPreferences();
  const audioLocale = options.audioLocale ?? 'da';
  const [voicesReady, setVoicesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void audio.waitForVoices().then(() => {
      if (!cancelled) setVoicesReady(true);
    });
    return () => {
      cancelled = true;
      audio.cancel();
    };
  }, [audio]);

  const audioEnabled = prefs.audioEnabled !== false;
  const audioAvailable =
    audioEnabled && voicesReady && audio.isAvailable();
  const showNoVoiceBanner = voicesReady && audioEnabled && !audio.isAvailable();

  const speak = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!audioAvailable || !trimmed) return;
      await audio.speak(trimmed, {
        rate: prefs.dictationRate,
        voiceId: prefs.dictationVoiceId,
        audioLocale,
      });
    },
    [audio, audioAvailable, prefs.dictationRate, prefs.dictationVoiceId, audioLocale],
  );

  const cancel = useCallback(() => {
    audio.cancel();
  }, [audio]);

  return {
    voicesReady,
    audioAvailable,
    showNoVoiceBanner,
    audioEnabled,
    speak,
    cancel,
  };
}
